<script setup lang="ts">
import { computed, unref, type MaybeRef } from 'vue';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';

export interface ChoosableAction {
  code: string;
  label: string;
  description?: string;
  disabled?: boolean;
  icon?: IconProp;
  badge?: MaybeRef<string | number | undefined>;
}

interface Props {
  title?: string;
  actions: ChoosableAction[];
  modelValue?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (event: 'update:modelValue', value: string): void
}>();

const selectAction = (action: ChoosableAction) => {
  emit('update:modelValue', action.code);
};

const preparedActions = computed(() => props.actions.map((action) => (
  {
    ...action,
    badge: unref(action.badge),
  }
)));
</script>

<template>
  <div class="modal-action-chooser">
    <h1 v-if="title">
      {{ title }}
    </h1>

    <div class="actions">
      <div
        v-for="action in preparedActions"
        :key="action.code"
        @click="selectAction(action)"
        :class="{ disabled: action.disabled, active: modelValue === action.code }">
        <font-awesome-icon
          v-if="action.icon"
          class="mb-1"
          :icon="action.icon" />

        {{ action.label }}

        <span
          class="badge"
          v-if="action.badge">
          <span>{{ action.badge }}</span>
        </span>

        <div
          v-if="action.description"
          class="text-regular text-s">
          {{ action.description }}
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.modal-action-chooser {
  padding-top: var(--space-4);
  text-align: center;

  h1 {
    margin-bottom: var(--space-3);
  }

  .actions {
    display: flex;
    justify-content: center;
    gap: var(--space-2);

    > div {
      border: 2px solid var(--color-primary);
      padding: var(--space-2) var(--space-3);
      border-radius: var(--border-radius);
      font-weight: var(--font-weight-bold);
      font-size: var(--font-size-m);
      transition: background var(--transition-duration-default) var(--transition-animation-default);
      cursor: pointer;
      background: var(--color-white);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      position: relative;

      &:hover, &.active {
        background: var(--color-primary);
      }

      &.disabled {
        opacity: 0.7;
        cursor: not-allowed;
        pointer-events: none;
      }
    }
  }

  + .content {
    padding-top: calc(var(--space-5) + var(--space-2));
    margin-top: calc(var(--space-5) + var(--space-2));
    position: relative;

    .upgrade-icon {
      margin-top: 0;
    }

    &:before {
      content: "";
      display: block;
      width: 35%;
      height: 1px;
      background: var(--color-black);
      opacity: 0.2;
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
    }
  }
}
</style>
